// C:\Users\home\Desktop\coding_react\damoa-qr\src\pages\en\QrPage_en.js
import React, { useState, useEffect, useMemo } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../QrPage.css";

function App() {
  const [url, setUrl] = useState("");
  const [qrValue, setQrValue] = useState("");
  const [darkMode, setDarkMode] = useState(false);

  // Options (minimal)
  const [size, setSize] = useState(224); // 128~512
  const [fgColor, setFgColor] = useState("#111827");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [includeMargin, setIncludeMargin] = useState(true);

  // CSR canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "QR Code Generator",
      description:
        "Enter a URL to generate a QR code and download it as PNG with TooLoves' QR Code Generator.",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "QR, QR Code, qrcode.react, QR PNG, QR download, TooLoves, web utility",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // Sync browser/mobile nav bar color with dark/light mode
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  const handleGenerate = (e) => {
    e?.preventDefault?.();
    const v = url.trim();
    if (!v) {
      alert("Please enter a web address.");
      return;
    }
    setQrValue(v);
  };

  const handleDownload = () => {
    const canvas = document.getElementById("qr-canvas");
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = "qrcode.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setUrl("");
    setQrValue("");
  };

  // Open native share sheet on share button click
  const handleShare = async () => {
    if (!qrValue) return;

    const isShareCanceled = (err) => {
      if (!err) return false;
      const name = err.name || "";
      const msg = (err.message || "").toLowerCase();
      return (
        name === "AbortError" ||
        name === "NotAllowedError" ||
        msg.includes("abort") ||
        msg.includes("cancel")
      );
    };

    const canvas = document.getElementById("qr-canvas");

    // 1) Try file sharing (PNG if possible)
    if (canvas && "toBlob" in canvas && typeof navigator?.canShare === "function") {
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      if (blob) {
        const file = new File([blob], "qrcode.png", { type: "image/png" });
        const shareDataWithFile = {
          files: [file],
          title: "QR Code",
          text: qrValue,
        };

        if (navigator.canShare(shareDataWithFile)) {
          try {
            await navigator.share(shareDataWithFile);
            return; // success
          } catch (err) {
            if (isShareCanceled(err)) return; // user canceled
            // otherwise fall through
          }
        }
      }
    }

    // 2) If file sharing not supported: share URL
    if (navigator.share) {
      try {
        await navigator.share({
          title: "QR Code",
          text: "Open the link below.",
          url: qrValue,
        });
        return; // success
      } catch (err) {
        if (isShareCanceled(err)) return; // user canceled
        // otherwise fall through
      }
    }

    // 3) Fallback: copy URL to clipboard
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(qrValue);
        alert("Sharing sheet isn’t supported, so the link was copied to your clipboard.");
      } else {
        alert("Sharing isn’t supported in this browser.");
      }
    } catch {
      alert("Sharing isn’t supported in this browser.");
    }
  };

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "en" }}>
        <title>QR Code Generator | TooLoves</title>
        <meta
          name="description"
          content="Generate a QR code from a URL and download it as PNG."
        />
        <meta
          name="keywords"
          content="QR, QR Code, qrcode.react, QR PNG, QR download, TooLoves, web utility"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Initial theme-color; synced at runtime via useEffect */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="QR Code Generator | TooLoves" />
        <meta
          property="og:description"
          content="Quickly create a QR code from a website URL and save as PNG."
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-qr.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="QR Code Generator | TooLoves" />
        <meta
          name="twitter:description"
          content="Quickly create a QR code from a website URL and save as PNG."
        />
        <meta name="twitter:image" content="/og-qr.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* Top App Bar */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/en" className="brand-link" aria-label="Go to TooLoves home (English)">
            TooLoves
          </Link>
          <button
            className="mode-toggle"
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? "Light mode" : "Dark mode"}
          </button>
        </div>
      </header>

      {/* Main */}
      <main className="container">
        <section className="generator-card">
          <h2 className="card-title">QR Code Generator</h2>
          <p className="card-subtitle">
            Enter a URL and click Generate to preview and download below.
          </p>

          <form className="form" onSubmit={handleGenerate}>
            <label htmlFor="url" className="form-label sr-only">
              Website URL
            </label>
            <div className="input-row">
              <input
                id="url"
                type="text"
                placeholder="Enter website URL here"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoComplete="off"
                inputMode="url"
              />
              <button type="submit" className="generate-btn">
                Generate QR
              </button>
            </div>

            <div className="options-row">
              <div className="field">
                <label className="form-label">Size</label>
                <input
                  type="range"
                  min="128"
                  max="512"
                  step="32"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                />
                <div className="hint">{size}px</div>
              </div>

              <div className="field">
                <label className="form-label">Foreground</label>
                <input
                  type="color"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                />
              </div>

              <div className="field">
                <label className="form-label">Background</label>
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                />
              </div>

              <div className="field switch">
                <label className="form-label">Include margin</label>
                <input
                  type="checkbox"
                  checked={includeMargin}
                  onChange={(e) => setIncludeMargin(e.target.checked)}
                />
              </div>
            </div>
          </form>

          <div className="preview-card">
            {qrValue ? (
              <>
                <div className="qr-wrap">
                  <QRCodeCanvas
                    id="qr-canvas"
                    value={qrValue}
                    size={size}
                    bgColor={bgColor}
                    fgColor={fgColor}
                    includeMargin={includeMargin}
                  />
                </div>
                <p className="qr-url">{qrValue}</p>
                <div className="actions">
                  <button type="button" onClick={handleDownload}>
                    Download PNG
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleReset}
                  >
                    Reset
                  </button>

                  {/* Share icon button (no text) */}
                  <button
                    type="button"
                    className="btn-secondary icon-btn"
                    onClick={handleShare}
                    aria-label="Share"
                    title="Share"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      {/* Feather/Lucide share-2 icon */}
                      <circle cx="18" cy="5" r="3"></circle>
                      <circle cx="6" cy="12" r="3"></circle>
                      <circle cx="18" cy="19" r="3"></circle>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                  </button>
                </div>
              </>
            ) : (
              <p className="placeholder">
                Enter a URL and click “Generate QR”.
              </p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="Legal links">
          <Link to="/en/terms" className="footer-link">Terms of Service</Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <Link to="/en/privacy" className="footer-link">Privacy Policy</Link>
        </nav>
      </footer>
    </div>
  );
}

export default App;